import {
	type EditorState as CodeMirrorEditorState,
	Transaction as CodeMirrorTransaction,
} from "@codemirror/state";
import isString from "lodash-es/isString";
import {
	type EditorState as ProseMirrorEditorState,
	Transaction as ProseMirrorTransaction,
} from "prosemirror-state";
import { assign, enqueueActions, fromPromise, sendParent, setup } from "xstate";

import { parsePage } from "@moment/api-collab/markdown";
import { Logger } from "@moment/logging";
import { schema } from "@moment/prosemirror-schema";

import { isDesktopAppMode } from "~/auth/interop/common";
import { createEditorState } from "~/components/canvas/easel/editor-state/createEditorState";
import { createMarkdownEditorState } from "~/components/canvas/easel/editor-state/createMarkdownEditorState";
import {
	type Cell,
	type CellInfo,
	type CellMap,
	equals,
	proseMirrorEditorStateToCells,
} from "~/models/cell";
import { emitCell } from "~/runtime/cell-compiler/emit";
import { getDocsApiUrl } from "~/state/api/docsapi";
import { type StoredPage } from "~/state/types/editors.types";

import { filesystemApi } from "../../api/filesystem";
import { getAccessToken } from "../authentication";

const logger = new Logger("page.machine");

const loadPageContentsFromDisk = fromPromise<
	{ parsed: StoredPage; raw: string },
	{ documentPath: string; pageId: string }
>(async ({ input }) => {
	return filesystemApi.page.contents.read(input.documentPath, input.pageId);
});

const loadPageContentsFromRemote = fromPromise<
	StoredPage,
	{
		workspaceName?: string;
		documentId: string;
		pageId: string;
	}
>(async ({ input }) => {
	const { workspaceName, documentId, pageId } = input;

	const accessToken = await getAccessToken();

	const headers = new Headers();
	headers.set("Accept", "application/json");

	if (accessToken) {
		headers.set("Authorization", `Bearer ${accessToken}`);
	}

	const response = await fetch(
		`${getDocsApiUrl()}/api/v1/repos/${workspaceName}/${documentId}/raw/pages/${pageId}/index.md?ref=main`,
		{ headers }
	);
	if (!response.ok) {
		throw new Error("Failed to fetch public page metadata");
	}

	const text = await response.text();
	const { doc: jsonDoc, metadata } = await parsePage(text);

	return {
		doc: jsonDoc,
		metadata: {
			id: pageId,
			slug: pageId,
			title: "",
			archived: false,
			parentPageID: null,
			...metadata,
		},
	};
});

const writeEditorStateToDisk = fromPromise(
	async ({
		input,
	}: {
		input: {
			pageId: string;
			documentPath: string;
			editorState: ProseMirrorEditorState | CodeMirrorEditorState;
		};
	}) => {
		if (!isDesktopAppMode()) {
			return Promise.reject();
		}

		return filesystemApi.page.contents.write(
			input.documentPath,
			input.pageId,
			input.editorState.doc
		);
	}
);

const writeMarkdownEditorStateToDisk = fromPromise(
	async ({
		input,
	}: {
		input: { pageId: string; documentPath: string; editorState: CodeMirrorEditorState };
	}) => {
		return filesystemApi.page.contents.write(
			input.documentPath,
			input.pageId,
			input.editorState.doc
		);
	}
);

async function compileCell(cell: Cell, env: MomentAppEnv): Promise<CellInfo> {
	try {
		const emitInfo = await emitCell(cell, {}, env);

		if (emitInfo.kind === "StaticError") {
			return {
				cell,
				status: "error",
				message: emitInfo.message,
			};
		}

		return {
			cell,
			emitInfo,
			status: "executable",
		};
	} catch (error) {
		return {
			cell,
			status: "error",
			message: error instanceof Error ? error.message : "Unknown error",
		};
	}
}

const compileCells = fromPromise(
	async ({
		input,
	}: {
		input: {
			pageId: string;
			previousCells: CellMap;
			editorState: ProseMirrorEditorState;
		};
	}): Promise<CellMap> => {
		const { previousCells, editorState } = input;
		const env = process.env["APP_ENV"];

		const nextCells = proseMirrorEditorStateToCells(
			editorState,
			(node) => node.type === schema.nodes.compute_cell
		);
		const nextCellIDs = new Set(nextCells.map((cell) => cell.id).filter(isString));

		// Compile cells that have changed
		const cellsToCompile = nextCells.filter(
			(cell) => !!cell.id && !equals(cell, previousCells[cell.id]?.cell || {})
		);

		const anyCellsDeleted =
			nextCellIDs.difference(new Set(Object.keys(previousCells))).size > 0;

		if (!anyCellsDeleted && cellsToCompile.length === 0) {
			return previousCells;
		}

		const newCompiledCells = Object.fromEntries(
			await Promise.all(
				cellsToCompile.map(async (cell) => {
					return [cell.id, await compileCell(cell, env)] as const;
				})
			)
		);

		// Merge delete operations with new compiled cells
		const compiledCells: CellMap = {};

		for (const id of nextCellIDs) {
			const newlyCompiledCell = newCompiledCells[id];
			if (newlyCompiledCell) {
				compiledCells[id] = newlyCompiledCell;
				continue;
			}

			const previousCell = previousCells[id];
			if (previousCell) {
				compiledCells[id] = previousCell;
				continue;
			}
		}

		return compiledCells;
	}
);

export const pageMachine = setup({
	types: {
		context: {} as {
			workspaceName?: string;
			documentId: string;
			pageId: string;
			pageData?: StoredPage;
			documentPath?: string;
			schemaVersion: string;
			editorMode: "rich" | "markdown";
			theme: "light" | "dark";
			editorState?: ProseMirrorEditorState;
			markdownEditorState?: CodeMirrorEditorState;
			compiledCells: CellMap;
			compilationError: Error | null;
			lastTransactionAppliedId: string | null;
			lastTransactionPersistedId: string | null;
			error: Error | null;
		},
		input: {} as {
			workspaceName?: string;
			documentId: string;
			pageId: string;
			documentPath?: string;
		},
		events: {} as
			| { type: "document.cloned"; payload: { documentPath: string } }
			| {
					type: "page.opened";
					payload?: { mode: "rich" | "markdown"; theme: "light" | "dark" };
			  }
			| { type: "page.compute" }
			| { type: "page.closed" }
			| { type: "page.archived" }
			| { type: "page.unarchived" }
			| { type: "page.nameChanged"; payload: { newName: string } }
			| { type: "page.slugChanged"; payload: { newSlug: string } }
			| {
					type: "page.transactionDispatched";
					payload: {
						transaction: ProseMirrorTransaction | CodeMirrorTransaction;
						timestamp: Date;
						transactionId: string;
					};
			  }
			| { type: "page.refresh" },
	},
	actors: {
		loadPageContentsFromDisk,
		loadPageContentsFromRemote,
		writeEditorStateToDisk,
		writeMarkdownEditorStateToDisk,
		compileCells,
	},
	guards: {
		isDesktopAppMode: () => {
			return isDesktopAppMode();
		},

		pageExistsOnDisk: ({ context }) => {
			return Boolean(context.documentPath);
		},

		lastTransactionWasNotPersisted: ({ context }) => {
			return context.lastTransactionPersistedId !== context.lastTransactionAppliedId;
		},

		editorModeIsRich: ({ context }) => {
			return context.editorMode === "rich";
		},

		editorModeIsMarkdown: ({ context }) => {
			return context.editorMode === "markdown";
		},
	},
	actions: {
		applyTransaction: assign(({ context, event }) => {
			if (event.type !== "page.transactionDispatched") {
				return {};
			}

			const { transaction, transactionId } = event.payload;

			if (transaction instanceof ProseMirrorTransaction) {
				if (!context.editorState) {
					logger.error("applyTransaction: No editor state found", {
						context,
						transaction,
					});
					return {};
				}

				return {
					editorState: context.editorState.apply(transaction),
					lastTransactionAppliedId: transactionId,
				};
			}

			if (transaction instanceof CodeMirrorTransaction) {
				if (!context.markdownEditorState) {
					logger.error("applyTransaction: No markdown editor state found", {
						context,
						transaction,
					});
					return {};
				}

				return {
					markdownEditorState: transaction.state,
					lastTransactionAppliedId: transactionId,
				};
			}

			return {};
		}),

		clearEditorState: assign(() => ({
			editorState: undefined,
			markdownEditorState: undefined,
			compiledCells: undefined,
			lastTransactionAppliedId: null,
			lastTransactionPersistedId: null,
		})),
	},
}).createMachine({
	id: "page",
	type: "parallel",
	context: ({ input }) => ({
		workspaceName: input.workspaceName,
		documentId: input.documentId,
		pageId: input.pageId,
		editorMode: "rich",
		theme: "light",
		compiledCells: {},
		compilationError: null,
		schemaVersion: "v1alpha1",
		documentPath: input.documentPath,
		lastTransactionAppliedId: null,
		lastTransactionPersistedId: null,
		error: null,
	}),
	states: {
		error: {
			type: "final",
		},

		data: {
			initial: "idle",
			states: {
				idle: {},

				fetching: {
					always: [
						{
							guard: "pageExistsOnDisk",
							target: "fetchingFromDisk",
						},
						{
							target: "fetchingFromRemote",
						},
					],
				},

				fetchingFromDisk: {
					invoke: {
						src: "loadPageContentsFromDisk",
						input: ({ context }) => ({
							documentPath: context.documentPath!,
							pageId: context.pageId,
						}),
						onDone: {
							actions: assign(({ context, event }) => ({
								pageData: event.output.parsed,
								editorState: createEditorState({
									workspaceName: context.workspaceName,
									documentId: context.documentId,
									pageId: context.pageId,
									doc: event.output.parsed.doc,
								}),
								markdownEditorState: createMarkdownEditorState(event.output.raw, {
									theme: context.theme,
								}),
							})),
							target: ["editing", "#page.compilation.compiling"],
						},
					},
				},
				fetchingFromRemote: {
					invoke: {
						src: "loadPageContentsFromRemote",
						input: ({ context }) => ({
							workspaceName: context.workspaceName,
							documentId: context.documentId,
							pageId: context.pageId,
						}),
						onDone: {
							target: ["editing", "#page.compilation.compiling"],
							actions: assign(({ context, event }) => ({
								pageData: event.output,
								editorState: createEditorState({
									workspaceName: context.workspaceName,
									documentId: context.documentId,
									pageId: context.pageId,
									doc: event.output.doc,
								}),
							})),
						},
						onError: {
							target: "#page.error",
							actions: assign(({ event }) => ({
								error: new Error("Failed to fetch page data", {
									cause: event.error,
								}),
							})),
						},
					},
				},

				editing: {
					on: {
						"page.opened": {
							target: "editing",
							reenter: true,
							actions: enqueueActions(({ context, enqueue, event }) => {
								enqueue.assign(
									event.payload?.mode !== undefined
										? {
												editorMode: event.payload?.mode,
											}
										: {}
								);

								// If the theme has changed, we need to recreate the editor state with the new extensions
								if (event.payload?.theme && context.theme !== event.payload.theme) {
									enqueue.assign({
										theme: event.payload?.theme,
										markdownEditorState: createMarkdownEditorState(
											context.markdownEditorState?.doc?.toString() ?? "",
											{ theme: event.payload?.theme }
										),
									});
								}
							}),
						},
						"page.nameChanged": {
							actions: [
								assign(({ context, event }) => ({
									pageData: {
										...context.pageData!,
										metadata: {
											...context.pageData!.metadata,
											title: event.payload.newName,
										},
									},
								})),
								sendParent(({ context, event }) => ({
									type: "page.nameChanged",
									payload: {
										pageId: context.pageId,
										newName: event.payload.newName,
									},
								})),
							],
						},
						"page.transactionDispatched": {
							actions: "applyTransaction",
							target: "editing",
							reenter: true,
						},
					},
				},

				// ! "Closed" is not used right now
				// TODO: Use this to close the page when the user navigates away from it
				// closed: {
				// 	after: {
				// 		30_000: {
				// 			description: "Clear the editor state and compiled cells out of memory",
				// 			target: "idle",
				// 			actions: "clearEditorState",
				// 		},
				// 	},
				// },
			},
			on: {
				"page.opened": [
					{
						actions: assign(({ event }) =>
							event.payload?.mode !== undefined
								? {
										editorMode: event.payload?.mode,
									}
								: {}
						),
						target: "#page.data.fetching",
					},
				],
			},
		},

		persistence: {
			initial: "idle",
			states: {
				idle: {
					on: {
						"page.transactionDispatched": {
							guard: "pageExistsOnDisk",
							target: "debouncing",
						},
					},
				},
				debouncing: {
					after: {
						2_000: [
							{ guard: "editorModeIsRich", target: "persistingEditorState" },
							{ guard: "editorModeIsMarkdown", target: "persistingMarkdown" },
						],
					},
				},
				persistingEditorState: {
					invoke: {
						src: "writeEditorStateToDisk",
						input: ({ context }) => ({
							pageId: context.pageId,
							documentPath: context.documentPath!,
							editorState: context.editorState!,
							transactionId: context.lastTransactionAppliedId!,
						}),
						onDone: {
							target: "#page.persistence.idle",
							actions: [
								assign(({ context, event }) => ({
									lastTransactionPersistedId: context.lastTransactionAppliedId,
									markdownEditorState: createMarkdownEditorState(
										event.output.raw,
										{ theme: context.theme }
									),
								})),
								sendParent(({ context }) => ({
									type: "page.changesPersisted",
									payload: {
										pageId: context.pageId,
										documentId: context.documentId,
									},
								})),
							],
						},
						onError: {
							target: "#page.persistence.idle",
						},
					},
					exit: sendParent({
						type: "page.persisted",
					}),
				},
				persistingMarkdown: {
					invoke: {
						src: "writeMarkdownEditorStateToDisk",
						input: ({ context }) => ({
							pageId: context.pageId,
							documentPath: context.documentPath!,
							editorState: context.markdownEditorState!,
							transactionId: context.lastTransactionAppliedId!,
						}),
						onDone: {
							target: "#page.persistence.idle",
							actions: [
								assign(({ context, event }) => ({
									lastTransactionPersistedId: context.lastTransactionAppliedId,
									editorState: createEditorState({
										workspaceName: context.workspaceName,
										documentId: context.documentId,
										pageId: context.pageId,
										doc: event.output.parsed,
									}),
								})),
								sendParent(({ context }) => ({
									type: "page.changesPersisted",
									payload: {
										pageId: context.pageId,
										documentId: context.documentId,
									},
								})),
							],
						},
						onError: {
							target: "#page.persistence.idle",
						},
					},
				},
			},
		},

		compilation: {
			initial: "idle",
			states: {
				idle: {
					on: {
						"page.transactionDispatched": { target: "debouncing" },
						"page.compute": { target: "compiling" },
					},
				},
				debouncing: {
					after: { 2_000: { target: "compiling" } },
					on: {
						"page.compute": {
							target: "compiling",
						},
					},
				},
				compiling: {
					entry: assign(() => ({
						compilationError: null,
					})),
					invoke: {
						src: "compileCells",
						input: ({ context }) => ({
							pageId: context.pageId,
							previousCells: context.compiledCells,
							editorState: context.editorState!,
						}),
						onDone: {
							target: "idle",
							actions: assign(({ event }) => ({
								compiledCells: event.output,
							})),
						},
						onError: {
							target: "idle",
							actions: assign(({ event }) => ({
								compilationError: new Error("Compilation failed", {
									cause: event.error,
								}),
							})),
						},
					},
				},
			},
		},
	},

	on: {
		"document.cloned": {
			actions: assign(({ event }) => ({
				documentPath: event.payload.documentPath,
			})),
		},
		"page.archived": {
			actions: sendParent(({ context }) => ({
				type: "page.archived",
				payload: {
					pageId: context.pageId,
				},
			})),
		},
		"page.unarchived": {
			actions: sendParent(({ context }) => ({
				type: "page.unarchived",
				payload: {
					pageId: context.pageId,
				},
			})),
		},
		"page.refresh": {
			target: ".data.fetching",
		},
	},
});
